/**
 * CallPage — the in-call orchestrator.
 *
 * Lifecycle:
 * 1. Connect to signaling
 * 2. Exchange SDP/ICE → establish peer connection
 * 3. Add local media (audio + video)
 * 4. Setup qr20 data channel → Qr20WebrtcSession
 * 5. (Sender) Show StreamSetup to configure deposit/rate/token
 * 6. Create streaming engine with user-configured params
 * 7. Start engine
 * 8. Render: video tiles, counter, controls, debug panel
 * 9. On end: stop engine, show settlement summary
 */

import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { WebRTCService } from '../modules/webrtc/webrtcService.js';
import { Qr20WebrtcSession, type Qr20WebrtcSessionOptions } from '@tokenstreaming/transport-webrtc';
import { createStreamingEngine } from '../modules/streaming/index.js';
import { useWallet } from '../modules/wallet/WalletContext.js';
import { useToast } from '../components/Toast.js';
import { VideoTile } from '../components/VideoTile.js';
import { StreamingCounter } from '../components/StreamingCounter.js';
import { CallControls } from '../components/CallControls.js';
import { DebugPanel } from '../components/DebugPanel.js';
import { SettlementSummary } from '../components/SettlementSummary.js';
import { StreamSetup, type StreamConfig } from '../components/StreamSetup.js';
import { getAppConfig } from '../config.js';
import { DEFAULT_STREAMING_PARAMS, getChainConfig, getAssetRef } from '@tokenstreaming/shared';
import { loadStreamSettings, resolveStreamConfig } from '../modules/streaming/streamSettings.js';
import type { StreamingEngine, EngineState, ChannelState } from '../modules/streaming/types.js';
import type { ConnectionState } from '../modules/webrtc/types.js';

interface Props {
  role: 'sender' | 'receiver';
  roomId: string;
  secret: string;
  signalingUrl: string;
  peerAddress: string;
  onEnd: () => void;
}

type CallPhase =
  | 'connecting'
  | 'media'
  | 'negotiating'
  | 'configuring'    // sender configures stream params
  | 'streaming'
  | 'closing'        // engine is closing
  | 'settling'       // on-chain settlement in progress
  | 'settled'
  | 'error';

export function CallPage({ role, roomId, secret, signalingUrl, peerAddress, onEnd }: Props): ReactNode {
  const { actions, chainId, status } = useWallet();
  const toast = useToast();
  const config = getAppConfig();

  const [phase, setPhase] = useState<CallPhase>('connecting');
  const [connectionState, setConnectionState] = useState<ConnectionState>('new');
  const [engineState, setEngineState] = useState<EngineState>('idle');
  const [channelState, setChannelState] = useState<ChannelState | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [error, setError] = useState('');
  const [txHash, setTxHash] = useState('');
  const [showSettlement, setShowSettlement] = useState(false);
  const [streamConfig, setStreamConfig] = useState<StreamConfig | null>(null);

  const webrtcRef = useRef<WebRTCService | null>(null);
  const engineRef = useRef<StreamingEngine | null>(null);
  const sessionRef = useRef<Qr20WebrtcSession | null>(null);
  const channelIdRef = useRef(`ch-${roomId.slice(0, 8)}-${Date.now()}`);

  const chain = getChainConfig(chainId);
  const address = status.state === 'unlocked' ? status.address : '';

  // ─── Orchestration ──────────────────────────────────────────────────────

  useEffect(() => {
    void startCall();
    return () => { cleanup(); };
  }, []);

  const startCall = async () => {
    try {
      setPhase('media');

      // WebRTCService.start() does everything:
      // getUserMedia → createPC → addTracks → dataChannels → signaling → SDP/ICE
      const isOfferer = role === 'sender';
      const webrtc = new WebRTCService({
        onRemoteStream: setRemoteStream,
        onDataChannelReady: handleDataChannelReady,
        onConnected: () => {
          setConnectionState('connected');
          setPhase('configuring');
        },
        onDisconnected: (reason) => {
          setConnectionState('failed');
          if (engineRef.current?.getState() === 'streaming') {
            toast.show('Peer disconnected', 'warning');
          }
        },
      }, isOfferer);

      webrtcRef.current = webrtc;

      setPhase('connecting');
      const stream = await webrtc.start(signalingUrl, roomId, secret);
      if (stream) setLocalStream(stream);

      setPhase('negotiating');
      // WebRTC negotiation happens inside start() via signaling callbacks.
      // When connection succeeds, onConnected fires → phase moves to 'configuring'.
    } catch (err) {
      handleCallError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  const handleDataChannelReady = useCallback((channel: RTCDataChannel) => {
    const sessionOpts: Qr20WebrtcSessionOptions = {
      crypto: { subtle: globalThis.crypto.subtle },
      bufferedAmountLowThreshold: 65536,
    };
    const session = new Qr20WebrtcSession(channel, sessionOpts);
    sessionRef.current = session;

    if (role === 'sender') {
      // Auto-start with persisted stream settings (no config gate)
      const settings = loadStreamSettings();
      const token = chain.tokens.find(t => t.address === settings.tokenAddress) ?? chain.tokens[0];
      const { deposit, ratePerSecAtomic } = resolveStreamConfig(settings, token?.decimals ?? 18);
      const cfg: StreamConfig = {
        tokenAddress: token?.address ?? '',
        tokenSymbol: token?.symbol ?? 'TOKEN',
        tokenDecimals: token?.decimals ?? 18,
        deposit,
        ratePerSecAtomic,
        receiverAddress: peerAddress || address,
      };
      setStreamConfig(cfg);
      void startEngine(session, cfg);
    } else {
      // Receiver: start engine immediately (will wait for channel-open from sender)
      void startEngine(session, null);
    }
  }, [role, chain, peerAddress, address]);

  // Kept for potential future use from settings overlay
  const handleStreamConfigConfirm = useCallback((cfg: StreamConfig) => {
    setStreamConfig(cfg);
    if (sessionRef.current) {
      void startEngine(sessionRef.current, cfg);
    }
  }, []);

  const startEngine = async (session: Qr20WebrtcSession, cfg: StreamConfig | null) => {
    const tokenAddr = cfg?.tokenAddress ?? chain.tokens[0]?.address ?? '';
    const tokenSym = cfg?.tokenSymbol ?? chain.tokens[0]?.symbol ?? 'TOKEN';
    const tokenDec = cfg?.tokenDecimals ?? chain.tokens[0]?.decimals ?? 18;
    const deposit = cfg?.deposit ?? 0n;
    const rate = cfg?.ratePerSecAtomic ?? 0n;
    const receiver = cfg?.receiverAddress ?? (peerAddress || address);

    const params = {
      channelId: channelIdRef.current,
      chain: chainId,
      asset: getAssetRef(chainId, tokenAddr),
      tokenAddress: tokenAddr,
      tokenDecimals: tokenDec,
      tokenSymbol: tokenSym,
      ratePerSecAtomic: rate,
      tickMs: DEFAULT_STREAMING_PARAMS.tickMs,
      settleEveryMs: DEFAULT_STREAMING_PARAMS.settleEveryMs,
      disputeWindowS: DEFAULT_STREAMING_PARAMS.defaultDisputeWindowS,
      deposit,
      resolverAddress: receiver,
      creatorAddress: address,
    };

    const signMessage = async (message: string): Promise<string> => {
      return actions.signMessage(message);
    };

    const engine = createStreamingEngine(role, params, session as any, signMessage, {
      onStateChange: (state) => {
        setEngineState(state);
        if (state === 'streaming') setPhase('streaming');
        if (state === 'closing') setPhase('closing');
        if (state === 'settling') setPhase('settling');
        if (state === 'settled') {
          setPhase('settled');
          setShowSettlement(true);
        }
        if (state === 'error') setPhase('error');
      },
      onBalanceUpdate: setChannelState,
      onError: handleCallError,
      onSettled: (hash) => {
        setTxHash(hash);
        setShowSettlement(true);
      },
    });

    engineRef.current = engine;
    try {
      await engine.start();
    } catch (err) {
      handleCallError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  // ─── Controls ─────────────────────────────────────────────────────────────

  const handleToggleAudio = useCallback(() => {
    const next = !audioEnabled;
    setAudioEnabled(next);
    webrtcRef.current?.toggleAudio(next);
  }, [audioEnabled]);

  const handleToggleVideo = useCallback(() => {
    const next = !videoEnabled;
    setVideoEnabled(next);
    webrtcRef.current?.toggleVideo(next);
  }, [videoEnabled]);

  const handleFlipCamera = useCallback(() => {
    void webrtcRef.current?.switchCamera();
  }, []);

  const handleEndCall = useCallback(async () => {
    const engine = engineRef.current;
    if (engine && (engine.getState() === 'streaming' || engine.getState() === 'negotiating')) {
      setPhase('closing');
      try {
        await engine.close();
      } catch {
        // Force cleanup on error
        cleanup();
        onEnd();
      }
    } else {
      cleanup();
      onEnd();
    }
  }, [onEnd]);

  const handleDismissSettlement = useCallback(() => {
    setShowSettlement(false);
    cleanup();
    onEnd();
  }, [onEnd]);

  const cleanup = () => {
    try { engineRef.current?.stop(); } catch { /* */ }
    try { sessionRef.current?.close(); } catch { /* */ }
    try { webrtcRef.current?.close(); } catch { /* */ }
    try { webrtcRef.current?.stop(); } catch { /* */ }
  };

  const handleCallError = (err: Error) => {
    setError(err.message);
    setPhase('error');
    toast.show(err.message, 'error');
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (phase === 'error') {
    return (
      <div className="screen">
        <div className="screen-body centered">
          <div className="error-icon">!</div>
          <h3>Call Error</h3>
          <p className="error-message">{error}</p>
          <button className="btn-primary" onClick={() => { cleanup(); onEnd(); }}>Return Home</button>
        </div>
      </div>
    );
  }

  if (phase === 'connecting' || phase === 'media' || phase === 'negotiating') {
    return (
      <div className="screen">
        <div className="screen-body centered">
          <div className="loading-spinner" />
          <p className="text-secondary">
            {phase === 'connecting' && 'Connecting to signaling server...'}
            {phase === 'media' && 'Requesting camera & microphone...'}
            {phase === 'negotiating' && 'Establishing peer connection...'}
          </p>
          <button className="btn-ghost" onClick={() => { cleanup(); onEnd(); }}>Cancel</button>
        </div>
      </div>
    );
  }

  // 'configuring' phase removed — sender auto-starts with persisted settings

  if (phase === 'closing') {
    return (
      <div className="screen">
        <div className="screen-body centered">
          <div className="loading-spinner" />
          <p className="text-secondary">Closing stream and preparing settlement...</p>
        </div>
      </div>
    );
  }

  if (phase === 'settling') {
    return (
      <div className="screen">
        <div className="screen-body centered">
          <div className="loading-spinner" />
          <h3>Settling On-Chain</h3>
          <p className="text-secondary">
            Submitting the final bilateral signed state and Distribution payouts to the Qr20Channel contract...
          </p>
          <p className="text-muted text-sm">This may take up to 30 seconds for block confirmation.</p>
        </div>
      </div>
    );
  }

  const tokenSym = streamConfig?.tokenSymbol ?? chain.tokens[0]?.symbol ?? 'TOKEN';
  const tokenDec = streamConfig?.tokenDecimals ?? chain.tokens[0]?.decimals ?? 18;
  const deposit = streamConfig?.deposit ?? 0n;

  return (
    <div className="call-page">
      {/* Video tiles */}
      <div className="video-grid">
        <VideoTile
          stream={remoteStream}
          label="Peer"
          address={peerAddress}
          connectionQuality={connectionState === 'connected' ? 'good' : 'fair'}
        />
        <VideoTile
          stream={localStream}
          isLocal
          muted
          label="You"
          address={address}
        />
      </div>

      {/* Streaming counter */}
      {engineRef.current && (
        <StreamingCounter
          engine={engineRef.current}
          tokenSymbol={tokenSym}
          tokenDecimals={tokenDec}
          deposit={deposit}
          role={role}
        />
      )}

      {/* Controls */}
      <CallControls
        audioEnabled={audioEnabled}
        videoEnabled={videoEnabled}
        isStreaming={engineState === 'streaming'}
        onToggleAudio={handleToggleAudio}
        onToggleVideo={handleToggleVideo}
        onEndCall={() => void handleEndCall()}
        onFlipCamera={handleFlipCamera}
      />

      {/* Debug panel (Ctrl+D) */}
      <DebugPanel
        engine={engineRef.current}
        connectionState={connectionState}
        signalingState={'connected'}
        channelId={channelIdRef.current}
      />

      {/* Settlement summary modal */}
      {showSettlement && channelState && (
        <SettlementSummary
          role={role}
          chainId={chainId}
          tokenSymbol={tokenSym}
          tokenDecimals={tokenDec}
          creatorAmount={channelState.creatorAmount}
          resolverAmount={channelState.resolverAmount}
          platformFee={(channelState.resolverAmount * BigInt(config.platformBps)) / 10000n}
          affiliateFee={(channelState.resolverAmount * BigInt(config.affiliateBps)) / 10000n}
          txHash={txHash}
          elapsedMs={engineRef.current?.getElapsedMs() ?? 0}
          totalUpdates={channelState.sequence}
          onDismiss={handleDismissSettlement}
        />
      )}
    </div>
  );
}
